/**
 * Hold org-invitation tokens outside the address bar / returnTo query so
 * login redirects and register navigation stay token-free while the tab session
 * can still complete the invite flow.
 *
 * Tokens remain XSS-readable while stashed (sessionStorage). Mitigations:
 * clear immediately after successful accept/register, clear on logout, and
 * expire unused stashes after a short TTL.
 */
const ORG_INVITE_TOKEN_SESSION_KEY = "org_invitation_token";

/** How long a stashed invite token remains readable in this tab. */
export const ORG_INVITE_TOKEN_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

type OrgInviteTokenStash = {
  token: string;
  expiresAt: number;
};

function isStashPayload(value: unknown): value is OrgInviteTokenStash {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.token === "string" &&
    typeof record.expiresAt === "number" &&
    Number.isFinite(record.expiresAt)
  );
}

function writeStash(payload: OrgInviteTokenStash): void {
  sessionStorage.setItem(
    ORG_INVITE_TOKEN_SESSION_KEY,
    JSON.stringify(payload)
  );
}

export function stashOrgInviteToken(token: string): void {
  const value = (token || "").trim();
  if (!value) return;
  try {
    writeStash({
      token: value,
      expiresAt: Date.now() + ORG_INVITE_TOKEN_TTL_MS,
    });
  } catch {
    // sessionStorage may be unavailable
  }
}

export function readStashedOrgInviteToken(): string {
  try {
    const raw = sessionStorage.getItem(ORG_INVITE_TOKEN_SESSION_KEY);
    if (!raw) return "";

    const trimmed = raw.trim();
    if (!trimmed) return "";

    // Preferred format: JSON with absolute expiry.
    if (trimmed.startsWith("{")) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (isStashPayload(parsed)) {
          const token = parsed.token.trim();
          if (!token || Date.now() > parsed.expiresAt) {
            clearStashedOrgInviteToken();
            return "";
          }
          return token;
        }
      } catch {
        clearStashedOrgInviteToken();
        return "";
      }
      clearStashedOrgInviteToken();
      return "";
    }

    // Legacy plain-string stash (pre-TTL): still usable, but migrate to TTL.
    const legacy = trimmed;
    writeStash({
      token: legacy,
      expiresAt: Date.now() + ORG_INVITE_TOKEN_TTL_MS,
    });
    return legacy;
  } catch {
    return "";
  }
}

export function clearStashedOrgInviteToken(): void {
  try {
    sessionStorage.removeItem(ORG_INVITE_TOKEN_SESSION_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
}
